import { Popconfirm } from 'antd';
import { SlRefresh } from 'react-icons/sl';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { useCallback, useRef, useState } from 'react';
import { MessageInstance } from 'antd/es/message/interface';
import { AppButton, Flex } from '../../common';
import { useSelfContext } from '@/context';
import { ReserveHttp } from '@/services';
import { useAppStore, useCartStore } from '@/store';
import { ReactComponent as IcPlusBasket } from '@/assets/images/ic-basket.svg';

export interface ReserveActionButtonsProps {
  isNewReserveMode?: boolean;
  meal: SelfMealEntity;
  reserve: UserWeekReserveEntity;
  selectedCount: number;
  messageAPI: MessageInstance;
  setSelectedCount: React.Dispatch<React.SetStateAction<number>>;
  onChangeSelf: () => void;
  locked: boolean;
  setLocked: (value: boolean) => any;
}

export default function ReserveActionButtons({
  isNewReserveMode = false,
  reserve,
  meal,
  onChangeSelf,
  selectedCount,
  setSelectedCount,
  messageAPI,
  locked,
  setLocked
}: ReserveActionButtonsProps) {



  const { fetchCredit } = useAppStore();
  const { currentSelf } = useSelfContext();
  const [t] = useTranslation(['common', 'reserve']);

  const addToCart = useCartStore(state => state.addToCart);
  const removeFromCart = useCartStore(state => state.removeFromCart);

  const [isIncrementing, setIsIncrementing] = useState(false);
  const [isDecrementing, setIsDecrementing] = useState(false);
  const [saleable, setSaleable] = useState(reserve?.saleableFreeFood);
  const [buyableFreeFood, setBuyableFreeFood] = useState(meal?.buyableFreeFood);
  const [forSale, setForSale] = useState(reserve?.forSale);
  const countRef = useRef<number>(selectedCount);

  const reservable = !meal.reserveRuleViolated;
  const cancelable = !meal.cancelRuleViolated;

  // TODO: check totalCount and meal.validTotalCount
  const isIncDisabled = selectedCount >= meal.validTotalCount || reserve.validMaxCount <= selectedCount;
  const isDecDisabled = selectedCount <= 0 || reserve.validMinCount - 1 >= selectedCount;

  const handleReserve = useCallback(async () => {
    setIsIncrementing(true);
    setLocked(true);
    setSelectedCount(c => {
      countRef.current = c + 1;
      reserve.selectedCount = countRef.current;
      return countRef.current;
    });
    try {
      if (currentSelf.freeRestaurant) {
        addToCart({ id: reserve.programId, food: reserve });
        messageAPI.success(t('reserve:messages.success-add-to-cart'));
        return;
      }
      console.log('isNewReserveMode', isNewReserveMode);

      if (isNewReserveMode) {
        await ReserveHttp.reserveNewAPI(reserve.programId, {
          count: 1,
        });
      }
      else {
        await ReserveHttp.reserve(reserve.programId, {
          foodTypeId: reserve.foodTypeId,
          mealTypeId: reserve.mealTypeId,
          selectedCount: countRef.current,
          freeFoodSelected: false,
        });
      }

      messageAPI.success(t('reserve:messages.success-reserve'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
      countRef.current = countRef.current - 1;
      reserve.selectedCount = countRef.current;
      setSelectedCount(countRef.current);
    }
    finally {
      setIsIncrementing(false);
      setLocked(false);
    }
  }, [countRef, setSelectedCount, currentSelf, reserve, addToCart, fetchCredit, messageAPI, t, isNewReserveMode]);

  const handleCancelReserve = useCallback(async () => {
    setIsDecrementing(true);
    setLocked(true);
    setSelectedCount(c => {
      if (isNewReserveMode) {
        if (c <= 0) {
          countRef.current = 0;
          return 0;
        }
        countRef.current = c - 1;
        reserve.selectedCount = countRef.current;
      }
      else {
        countRef.current = 0;
        reserve.selectedCount = 0;
      }
      return countRef.current;
    });
    try {
      if (currentSelf.freeRestaurant) {
        removeFromCart(reserve.programId);
        messageAPI.success(t('reserve:messages.success-remove-from-cart'));
        return;
      }
      await ReserveHttp.cancel(reserve.programId, {
        foodTypeId: reserve.foodTypeId,
        mealTypeId: reserve.mealTypeId,
        selectedCount: countRef.current,
        freeFoodSelected: false,
      });
      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
      countRef.current = countRef.current + 1;
      reserve.selectedCount = countRef.current;
      setSelectedCount(countRef.current);
    }
    finally {
      setIsDecrementing(false);
      setLocked(false);
    }
  }, [countRef, reserve, setSelectedCount, currentSelf, fetchCredit, messageAPI, removeFromCart, t]);

  const handleSellReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.sellReserve(reserveId);
      setSaleable(false);
      setForSale(true);
      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setSaleable, setForSale, fetchCredit, messageAPI, t]);

  const handleCancelSoldReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.cancelSoldReserve(reserveId);
      setSaleable(true);
      setForSale(false);
      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setSaleable, fetchCredit, messageAPI, t]);

  const handleBuyReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.buyFreeFood(reserveId);
      setSelectedCount(c => c + 1);
      setBuyableFreeFood(false);
      messageAPI.success(t('reserve:messages.success-non-subsidized-purchase'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setBuyableFreeFood, fetchCredit, messageAPI, setSelectedCount, t]);

  return (
    reservable && selectedCount === 0 ? (
      <AppButton
        size="small"
        appVariant="glass"
        className="button-reserve"
        onClick={handleReserve}
        loading={isIncrementing || isDecrementing}
        disabled={locked}
      >
        {t('reserve:labels.reserve')}
      </AppButton>
    ) :
      <div className="reserve-buttons">
        {reserve.changableSelf && (<ActionPlaceholder />)}
        <ActionPlaceholder>
          {forSale && reserve.sold === false && (
            <Popconfirm
              okText={t('common:labels.yes')}
              cancelText={t('common:labels.no')}
              onConfirm={() => handleCancelSoldReserve(reserve.id)}
              title={t('reserve:messages.confirm-sell')}
            >
              <IcPlusBasket
                fill="red"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
          {buyableFreeFood && (
            <Popconfirm
              okText={t('common:labels.yes')}
              onConfirm={() => handleBuyReserve(meal.programId)}
              title={(<>
                {t('reserve:messages.buy-non-subsidized')} <br />
                {t('reserve:messages.confirm-buy-non-subsidized-fee', { fee: meal.buyableFreeFoodToll })}
              </>)}
            >
              <IcPlusBasket
                fill="#3498db"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
          {saleable && (
            <Popconfirm
              okText={t('common:labels.yes')}
              onConfirm={() => handleSellReserve(reserve.id)}
              title={(<>
                {t('reserve:messages.put-sell-non-subsidized')} <br />
                {t('reserve:messages.confirm-put-sell-non-subsidized-fee', { fee: reserve.saleableFreeFoodToll })}
              </>)}
            >
              <IcPlusBasket
                fill="green"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
        </ActionPlaceholder>
        {reservable && (
          <AppButton
            appVariant="glass"
            onClick={handleReserve}
            disabled={isIncDisabled || isIncrementing || isDecrementing || locked}
            className="reserve-act-btn reserve-inc">
            {isIncrementing ?
              <LoadingOutlined style={{ fontSize: '16px' }} spin /> :
              <span className="flaticon-plus" />}
          </AppButton>
        )}
        <div className="reserve-selected-count">
          {!reservable && selectedCount === 0 ? '' :
            `${selectedCount} ${t('common:labels.no-')}`
          }
        </div>
        {cancelable && (
          <AppButton
            appVariant="glass"
            className="reserve-act-btn reserve-dec"
            disabled={isDecDisabled || isDecrementing || isIncrementing || locked}
            onClick={handleCancelReserve}
          >
            {isDecrementing ?
              <LoadingOutlined style={{ fontSize: '16px' }} spin /> :
              <span className="flaticon-remove" />}
          </AppButton>
        )}
        <ActionPlaceholder>
          {reserve.changableSelf && (
            <SlRefresh
              role="button"
              width={35}
              height={35}
              className="reserve-action"
              onClick={() => onChangeSelf()}
              style={{ fontSize: '1.25rem' }}
            />
          )}
        </ActionPlaceholder>
        {!cancelable && reserve.changableSelf && (<ActionPlaceholder />)}
      </div>
  );
}

function ActionPlaceholder({ children = <></> }: { children?: React.ReactElement | React.ReactElement[] }) {
  return (
    <Flex inlineFlex align="center" justify="center" className="w-[25px] h-[25px]">
      {children}
    </Flex>
  );
}
